import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import {
  Share2,
  Eye,
  Copy,
  Plus,
  Check,
  Settings,
  Trash2,
  X,
  Palette,
  Home,
  ExternalLink,
  QrCode,
  MapPin,
  DollarSign,
  Maximize,
  Loader,
  FolderPlus,
  GripVertical,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Layers,
  ArrowUpDown,
  Grid,
  List
} from 'lucide-react';
import { copyToClipboard } from '../utils/helpers';
import { buildOfferShareUrl } from '../utils/offerShareUtils';
import catalogService from '../services/catalogService';
import locationService from '../services/locationService';
import SearchableSelect from '../components/SearchableSelect';
import MultiSelect from '../components/MultiSelect';
import PermissionGate from '../components/PermissionGate';
import ErrorState from '../components/ErrorState';
import LimitBadge from '../components/LimitBadge';
import ConfirmDialog from '../components/ConfirmDialog/ConfirmDialog';
import GooglePagination from '../components/GooglePagination';
import { useToast } from '../context/ToastContext';
import './CatalogPage.css';

const DEFAULT_CATALOG_FIELDS = {
  price: true,
  area: true,
  rooms: true,
  location: true,
  images: true,
  description: true,
  contact: true,
  offerNature: true,
  status: true
};

const normalizeCatalogFields = (fields) => ({
  ...DEFAULT_CATALOG_FIELDS,
  ...(fields || {})
});

const DEFAULT_CATALOG_FILTERS = {
  propertyType: [],
  offerType: '',
  city: [],
  district: [],
  priceMin: '',
  priceMax: '',
  areaMin: '',
  areaMax: '',
  deedType: '',
  offerNature: '',
  rooms: '',
};

const normalizeCatalogFilters = (filters) => ({
  ...DEFAULT_CATALOG_FILTERS,
  ...(filters || {}),
  propertyType: Array.isArray(filters?.propertyType) ? filters.propertyType : (filters?.propertyType ? [filters.propertyType] : []),
  city: Array.isArray(filters?.city) ? filters.city : (filters?.city ? [filters.city] : []),
  district: Array.isArray(filters?.district) ? filters.district : (filters?.district ? [filters.district] : []),
});

const QUICK_FILTER_KEYS = ['propertyType', 'offerType', 'city', 'district', 'priceMin', 'priceMax', 'areaMin', 'areaMax'];

const createEmptyQuickFilter = (order = 0) => ({
  label: '',
  order,
  isActive: true,
  filters: {}
});

const normalizeQuickFilters = (quickFilters = []) => (
  Array.isArray(quickFilters)
    ? quickFilters
        .map((quickFilter, index) => ({
          ...quickFilter,
          label: quickFilter?.label || '',
          order: Number.isFinite(Number(quickFilter?.order)) ? Number(quickFilter.order) : index,
          isActive: typeof quickFilter?.isActive === 'boolean' ? quickFilter.isActive : true,
          filters: quickFilter?.filters && typeof quickFilter.filters === 'object' && !Array.isArray(quickFilter.filters)
            ? quickFilter.filters
            : {}
        }))
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
    : []
);

const cleanQuickFilterFilters = (filters = {}) => (
  QUICK_FILTER_KEYS.reduce((clean, key) => {
    const value = filters[key];
    if (Array.isArray(value)) {
      const filteredValues = value.filter(Boolean);
      if (filteredValues.length > 0) clean[key] = filteredValues;
      return clean;
    }
    if (value !== undefined && value !== null && value !== '') clean[key] = value;
    return clean;
  }, {})
);

const sanitizeQuickFilters = (quickFilters = []) => (
  normalizeQuickFilters(quickFilters)
    .filter((quickFilter) => quickFilter.label.trim())
    .map((quickFilter, index) => ({
      label: quickFilter.label.trim(),
      order: index,
      isActive: quickFilter.isActive === true,
      filters: cleanQuickFilterFilters(quickFilter.filters)
    }))
);

const CATALOG_STEPS = [
  { key: 'create', label: 'أنشئ', tooltip: 'أنشئ كتالوجاً باسم مميز واختر ثيماً يناسب علامتك التجارية' },
  { key: 'filter', label: 'صفّي', tooltip: 'حدّد فلاتر تلقائية — العروض تُضاف لوحدها بدون جهد' },
  { key: 'review', label: 'راجع', tooltip: 'شاهد نتيجة الفلاتر قبل المشاركة وتأكد من المحتوى' },
  { key: 'share',  label: 'شارك', tooltip: 'أرسل الرابط لعملائك مباشرة عبر واتساب أو انسخه' },
];

const CatalogPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  const [activeStepTooltip, setActiveStepTooltip] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFilterShare, setShowFilterShare] = useState(false);
  const [shareFilters, setShareFilters] = useState({});
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [editCatalogStep, setEditCatalogStep] = useState(1); // خطوات تعديل الكتالوج: 1-3
  const [openQuickFilterIndex, setOpenQuickFilterIndex] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [catalogToDelete, setCatalogToDelete] = useState(null);
  
  // Section management state
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionStep, setNewSectionStep] = useState(1); // 1=الاسم+النوع، 2=الفلاتر
  const [newSectionType, setNewSectionType] = useState('filter'); // 'filter' أو 'manual'
  const [newSectionFilters, setNewSectionFilters] = useState({ ...DEFAULT_CATALOG_FILTERS });
  const [parentSectionId, setParentSectionId] = useState(null); // null = قسم رئيسي، string = قسم فرعي
  const [expandedSections, setExpandedSections] = useState({});
  const [showDeleteSectionDialog, setShowDeleteSectionDialog] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [expandedCatalog, setExpandedCatalog] = useState(null);
  const [catalogDetail, setCatalogDetail] = useState(null);
  const [selectedCatalogView, setSelectedCatalogView] = useState(null); // view التفاصيل المنفصل
  const [openMenuId, setOpenMenuId] = useState(null); // زر 3 نقاط
  // FIX 4: track whether the open menu should open upward (button near bottom of screen)
  const [menuUpward, setMenuUpward] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortBy, setSortBy] = useState('newest'); // ترتيب العروض داخل الكتالوج المفتوح
  const [catalogListSort, setCatalogListSort] = useState('newest'); // ترتيب قائمة الكتالوجات
  const [showCatalogSortMenu, setShowCatalogSortMenu] = useState(false);
  const [adminViewMode, setAdminViewMode] = useState('list'); // 'list' أو 'gallery'
  const [createStep, setCreateStep] = useState(1); // خطوات إنشاء الكتالوج: 1، 2، 3
  // إعدادات الأقسام (اسم + فلاتر + يدوي)
  const [showSectionFiltersModal, setShowSectionFiltersModal] = useState(false);
  const [editingSectionFilters, setEditingSectionFilters] = useState(null); // { catalogRef, sectionId, sectionType }
  const [sectionFiltersDraft, setSectionFiltersDraft] = useState({ ...DEFAULT_CATALOG_FILTERS });
  const [sectionSettingsName, setSectionSettingsName] = useState('');
  const [manualOfferSearch, setManualOfferSearch] = useState('');
  const [manualCityFilter, setManualCityFilter] = useState('');
  const [manualTypeFilter, setManualTypeFilter] = useState('');
  const [manualSelectedOfferIds, setManualSelectedOfferIds] = useState([]); // IDs المختارة في وضع يدوي

  // API state
  const [catalogs, setCatalogs] = useState([]);
  const [offersPool, setOffersPool] = useState([]);
  const [offersPoolLoading, setOffersPoolLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [catalogPagination, setCatalogPagination] = useState({ current: 1, total: 1, count: 0 });
  const [locationCities, setLocationCities] = useState([]);
  const currentCatalogPage = catalogPagination.current;

  const fetchCatalogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await catalogService.getMyCatalogs({ page: currentCatalogPage, limit: 10 });
      if (response.success) {
        const catalogsData = response.data.catalogs || response.data || [];
        const normalizedCatalogs = catalogsData.map(catalog => ({
          ...catalog,
          fields: normalizeCatalogFields(catalog.fields || catalog.visibleFields),
          filters: normalizeCatalogFilters(catalog.filters),
          quickFilters: normalizeQuickFilters(catalog.quickFilters),
          sections: catalog.sections || []
        }));
        setCatalogs(normalizedCatalogs);
        const apiPagination = response.data?.pagination;
        if (apiPagination) {
          const nextPagination = {
            current: apiPagination.current || 1,
            total: apiPagination.total || 1,
            count: apiPagination.count || 0
          };

          setCatalogPagination((prev) => {
            if (
              prev.current === nextPagination.current &&
              prev.total === nextPagination.total &&
              prev.count === nextPagination.count
            ) {
              return prev;
            }
            return nextPagination;
          });
        }
      } else {
        setError(response.message || 'Failed to fetch catalogs');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch catalogs');
    } finally {
      setLoading(false);
    }
  }, [currentCatalogPage]);

  const fetchCatalogDetail = useCallback(async (catalogId) => {
    setDetailLoading(true);
    try {
      const response = await catalogService.getById(catalogId);
      if (response.success) {
        const catalog = response.data.catalog || response.data;
        setCatalogDetail({
          ...catalog,
          fields: normalizeCatalogFields(catalog.fields || catalog.visibleFields),
          filters: normalizeCatalogFilters(catalog.filters),
          quickFilters: normalizeQuickFilters(catalog.quickFilters),
          sections: catalog.sections || []
        });
      }
    } catch (err) {
      console.error('Failed to fetch catalog detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const fetchOffersPool = useCallback(async () => {
    setOffersPoolLoading(true);
    try {
      const catalogsResponse = await catalogService.getMyCatalogs({ page: 1, limit: 100 });
      if (catalogsResponse.success) {
        const catalogsData = catalogsResponse.data?.catalogs || catalogsResponse.data || [];
        const defaultCatalog = catalogsData.find((c) => c.isDefault);

        if (defaultCatalog) {
          const detailResponse = await catalogService.getById(defaultCatalog._id || defaultCatalog.id);
          if (detailResponse.success) {
            const defaultCatalogDetail = detailResponse.data?.catalog || detailResponse.data || {};
            setOffersPool(defaultCatalogDetail.offers || []);
            return;
          }
        }

        setOffersPool([]);
      }
    } catch (err) {
      setOffersPool([]);
    } finally {
      setOffersPoolLoading(false);
    }
  }, []);

  useEffect(() => { fetchCatalogs(); }, [fetchCatalogs]);
  useEffect(() => { fetchOffersPool(); }, [fetchOffersPool]);

  // قفل تمرير الصفحة عند فتح أي مودال
  useEffect(() => {
    const anyOpen = showCreateModal || showEditModal || showDeleteDialog || showShareModal;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showCreateModal, showEditModal, showDeleteDialog, showShareModal]);

  // إغلاق قائمة 3 نقاط عند النقر خارجها
  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  useEffect(() => {
    locationService.getAllCities().then((res) => {
      const allCities = res?.data?.cities || [];
      const priorityCities = ['الجبيل', 'الدمام'];
      const sorted = [
        ...priorityCities.map(name => allCities.find(c => (c.arLabel || c.label) === name)).filter(Boolean),
        ...allCities.filter(c => !priorityCities.includes(c.arLabel || c.label)),
      ];
      setLocationCities(sorted);
    }).catch(() => {});
  }, []);

  const [newCatalog, setNewCatalog] = useState({
    name: '', description: '', slug: '', theme: 'default',
    fields: { ...DEFAULT_CATALOG_FIELDS },
    filters: { ...DEFAULT_CATALOG_FILTERS },
    sections: []
  });

  const themes = [
    { id: 'default', name: 'أخضر غامق', color: '#1a5f4a' },
    { id: 'elegant', name: 'كحلي', color: '#2c3e50' },
    { id: 'modern', name: 'أزرق', color: '#3498db' },
    { id: 'warm', name: 'ذهبي', color: '#c4a35a' }
  ];

  const propertyTypes = [
    { value: 'villa', label: 'فيلا' },
    { value: 'apartment', label: 'شقة' },
    { value: 'land', label: 'أرض' },
    { value: 'building', label: 'عمارة' },
    { value: 'duplex', label: 'دوبلكس' },
    { value: 'commercial', label: 'تجاري' },
    { value: 'farm', label: 'مزرعة' },
    { value: 'rest_house', label: 'استراحة' },
    { value: 'floor', label: 'دور' },
    { value: 'shop', label: 'محل' },
    { value: 'warehouse', label: 'مستودع' },
    { value: 'studio', label: 'استوديو' }
  ];

  const getPropertyTypeColor = (type) => {
    const colors = {
      villa: { background: '#ecfeff', color: '#0e7490' },
      apartment: { background: '#eef2ff', color: '#4338ca' },
      land: { background: '#f0fdf4', color: '#166534' },
      building: { background: '#f8fafc', color: '#334155' },
      duplex: { background: '#fdf4ff', color: '#a21caf' },
      commercial: { background: '#fff7ed', color: '#c2410c' },
      farm: { background: '#ecfccb', color: '#3f6212' },
      rest_house: { background: '#fef3c7', color: '#92400e' },
      floor: { background: '#e0f2fe', color: '#075985' },
      shop: { background: '#ede9fe', color: '#5b21b6' },
      warehouse: { background: '#f1f5f9', color: '#334155' },
      studio: { background: '#f5f3ff', color: '#6d28d9' }
    };
    return colors[type] || { background: '#f1f5f9', color: '#334155' };
  };

  const getStatusColor = (status) => {
    const colors = {
      available: { background: '#dcfce7', color: '#166534' },
      reserved: { background: '#fef3c7', color: '#92400e' },
      sold: { background: '#fee2e2', color: '#991b1b' },
      rented: { background: '#e0f2fe', color: '#075985' }
    };
    return colors[status] || { background: '#f1f5f9', color: '#334155' };
  };

  const formatPrice = (price) => `${Number(price || 0).toLocaleString()} ر.س`;
  const formatArea = (area) => `${Number(area || 0).toLocaleString()} م²`;

  const sanitizeCatalogFilters = (rawFilters) => {
    const normalized = normalizeCatalogFilters(rawFilters);

    const cleanRangeValue = (value) => {
      if (value === '' || value === null || value === undefined) return '';
      const numeric = Number(value);
      if (Number.isNaN(numeric) || numeric <= 0) return '';
      return numeric;
    };

    return {
      offerType: normalized.offerType || '',
      propertyType: Array.isArray(normalized.propertyType) ? normalized.propertyType : [],
      city: Array.isArray(normalized.city) ? normalized.city : [],
      district: Array.isArray(normalized.district) ? normalized.district : [],
      priceMin: cleanRangeValue(normalized.priceMin),
      priceMax: cleanRangeValue(normalized.priceMax),
      areaMin: cleanRangeValue(normalized.areaMin),
      areaMax: cleanRangeValue(normalized.areaMax),
      deedType: normalized.deedType || '',
      offerNature: normalized.offerNature || '',
      rooms: normalized.rooms || '',
    };
  };

  const handleCreateCatalog = useCallback(async () => {
    if (!newCatalog.name) {
      toast.warning('يرجى إدخال اسم الكتالوج');
      return;
    }
    setSaving(true);
    try {
      const dataToSend = {
        ...newCatalog,
        visibleFields: newCatalog.fields,
        filters: sanitizeCatalogFilters(newCatalog.filters)
      };
      const response = await catalogService.create(dataToSend);
      if (response.success) {
        fetchCatalogs();
        fetchOffersPool();
        setShowCreateModal(false);
        setNewCatalog({
          name: '', description: '', slug: '', theme: 'default',
          fields: { ...DEFAULT_CATALOG_FIELDS },
          filters: { ...DEFAULT_CATALOG_FILTERS },
          sections: []
        });
        toast.success('تم إنشاء الكتالوج بنجاح!');
      } else {
        toast.error(response.message || 'فشل في إنشاء الكتالوج');
      }
    } catch (err) {
      toast.error(err.message || 'فشل في إنشاء الكتالوج');
    } finally { setSaving(false); }
  }, [newCatalog, fetchCatalogs, fetchOffersPool, toast]);

  const handleCopyLink = async (catalog) => {
    const link = `${window.location.origin}/c/${catalog.slug}`;
    await copyToClipboard(link);
    setCopiedLink(catalog._id || catalog.id);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const handleOpenDeleteDialog = (catalog) => {
    setCatalogToDelete(catalog);
    setShowDeleteDialog(true);
  };

  const handleConfirmDeleteCatalog = async () => {
    if (!catalogToDelete) return;
    const id = catalogToDelete._id || catalogToDelete.id;
    setSaving(true);
    try {
      const response = await catalogService.delete(id);
      if (response.success) {
        fetchCatalogs();
        if (expandedCatalog === id) { setExpandedCatalog(null); setCatalogDetail(null); }
        toast.success('تم حذف الكتالوج بنجاح!');
      } else { toast.error(response.message || 'فشل في حذف الكتالوج'); }
    } catch (err) { toast.error(err.message || 'فشل في حذف الكتالوج'); }
    finally { setSaving(false); setShowDeleteDialog(false); setCatalogToDelete(null); }
  };

  const handleEditCatalog = (catalog) => {
    const normalizedCatalog = {
      ...catalog,
      fields: normalizeCatalogFields(catalog.fields || catalog.visibleFields),
      filters: normalizeCatalogFilters(catalog.filters),
      quickFilters: normalizeQuickFilters(catalog.quickFilters)
    };
    setEditingCatalog(normalizedCatalog);
    setEditCatalogStep(1); // ابدأ دائماً من الخطوة الأولى
    setOpenQuickFilterIndex(null);
    setShowEditModal(true);
  };

  const handleSaveEdit = useCallback(async () => {
    if (!editingCatalog || !editingCatalog.name) { toast.warning('يرجى إدخال اسم الكتالوج'); return; }
    if (!editingCatalog.slug) { toast.warning('الرابط المخصص لا يمكن أن يكون فارغاً'); return; }
    const quickFiltersToSave = sanitizeQuickFilters(editingCatalog.quickFilters);
    const quickFilterWithoutCondition = quickFiltersToSave.find((quickFilter) => Object.keys(quickFilter.filters).length === 0);
    if (quickFilterWithoutCondition) {
      toast.warning('اختر شرطًا واحدًا على الأقل للفلتر.');
      return;
    }
    setSaving(true);
    try {
      const { _id, createdAt, updatedAt, offers, isDefault, fields, ...catalogData } = editingCatalog;
      const dataToSend = {
        ...catalogData,
        visibleFields: fields,
        filters: sanitizeCatalogFilters(editingCatalog.filters),
        quickFilters: quickFiltersToSave
      };
      const response = await catalogService.update(editingCatalog._id || editingCatalog.id, dataToSend);
      if (response.success) {
        fetchCatalogs();
        fetchOffersPool();
        if (expandedCatalog === (editingCatalog._id || editingCatalog.id)) {
          fetchCatalogDetail(editingCatalog._id || editingCatalog.id);
        }
        // تحديث selectedCatalogView بالبيانات الجديدة دون إغلاقه
        if (selectedCatalogView && (selectedCatalogView._id || selectedCatalogView.id) === (editingCatalog._id || editingCatalog.id)) {
          setSelectedCatalogView(prev => ({ ...prev, ...editingCatalog }));
        }
        setShowEditModal(false);
        setEditingCatalog(null);
        toast.success('تم تحديث الكتالوج بنجاح!');
      } else { toast.error(response.message || 'فشل في تحديث الكتالوج'); }
    } catch (err) { toast.error(err.message || 'فشل في تحديث الكتالوج'); }
    finally { setSaving(false); }
  }, [editingCatalog, fetchCatalogs, fetchOffersPool, fetchCatalogDetail, expandedCatalog, selectedCatalogView, toast]);

  const handleShareCatalog = (catalog) => { setSelectedCatalog(catalog); setShowShareModal(true); setShowFilterShare(false); setShareFilters({}); };
  const handleCloseShareModal = () => { setShowShareModal(false); setShowFilterShare(false); setShareFilters({}); };
  const handleOpenCatalogLink = (catalog) => { window.open(`${window.location.origin}/c/${catalog.slug}`, '_blank'); };
  // Offer links prefer the marketer route, without carrying catalog filter parameters.
  const getCatalogOfferShareUrl = (catalog, offer) => buildOfferShareUrl({
    username: user?.username,
    catalogSlug: catalog?.slug,
    offerId: offer?._id || offer?.id
  });
  const handleOpenCatalogOfferLink = (catalog, offer) => {
    const url = getCatalogOfferShareUrl(catalog, offer);
    if (!url) return;
    window.open(url, '_blank');
  };
  const handleShareWhatsApp = (catalog) => {
    const link = `${window.location.origin}/c/${catalog.slug}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(`${catalog.name}\n${catalog.description}\n\n${link}`)}`, '_blank');
  };
  const handleShareTwitter = (catalog) => {
    const link = `${window.location.origin}/c/${catalog.slug}`;
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}&text=${encodeURIComponent(`${catalog.name} - ${catalog.description}`)}`, '_blank');
  };
  const handleDownloadQR = (catalog) => {
    const link = `${window.location.origin}/c/${catalog.slug}`;
    window.open(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(link)}`, '_blank');
    toast.success('تم فتح QR Code في نافذة جديدة');
  };

  const handleExpandCatalog = (catalog) => {
    const id = catalog._id || catalog.id;
    if (expandedCatalog === id) { setExpandedCatalog(null); setCatalogDetail(null); }
    else { setExpandedCatalog(id); fetchCatalogDetail(id); }
  };

  const handleOpenCatalogView = (catalog) => {
    const id = catalog._id || catalog.id;
    setSelectedCatalogView(catalog);
    setExpandedCatalog(id);
    setExpandedSections({}); // الأقسام مخفية افتراضياً
    fetchCatalogDetail(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseCatalogView = () => {
    setSelectedCatalogView(null);
    setExpandedCatalog(null);
    setCatalogDetail(null);
    setExpandedSections({});
  };

  const handleAddSection = async (catalog) => {
    if (!newSectionName.trim()) return;
    const id = catalog._id || catalog.id;
    const currentSections = (catalogDetail?.sections || catalog.sections || []).map(s => ({ ...s }));
    const sectionFiltersToSave = newSectionType === 'filter' ? sanitizeCatalogFilters(newSectionFilters) : {};
    const newSectionData = { name: newSectionName.trim(), order: 0, offers: [], filters: sectionFiltersToSave, sectionType: newSectionType };
    let updatedSections;
    if (parentSectionId) {
      // إضافة قسم فرعي داخل القسم الأب
      updatedSections = currentSections.map(s => {
        if ((s._id || s.id) === parentSectionId) {
          const existingSubs = s.subSections || [];
          return { ...s, subSections: [...existingSubs, { ...newSectionData, order: existingSubs.length }] };
        }
        return s;
      });
    } else {
      // إضافة قسم رئيسي
      updatedSections = [...currentSections, { ...newSectionData, order: currentSections.length }];
    }
    setSaving(true);
    try {
      const response = await catalogService.update(id, { sections: updatedSections });
      if (response.success) {
        fetchCatalogs();
        await fetchCatalogDetail(id);
        setNewSectionName('');
        setNewSectionFilters({ ...DEFAULT_CATALOG_FILTERS });
        setNewSectionType('filter');
        setNewSectionStep(1);
        setParentSectionId(null);
        setShowAddSectionModal(false);
        toast.success(parentSectionId ? 'تم إضافة القسم الفرعي بنجاح!' : 'تم إضافة القسم بنجاح!');
      } else { toast.error(response.message || 'فشل في إضافة القسم'); }
    } catch (err) { toast.error(err.message || 'فشل في إضافة القسم'); }
    finally { setSaving(false); }
  };


  const handleDeleteSection = async () => {
    if (!sectionToDelete || !selectedCatalog) return;
    const id = selectedCatalog._id || selectedCatalog.id;
    const sections = catalogDetail?.sections || selectedCatalog.sections || [];
    const updatedSections = sections.filter(s => (s._id || s.id) !== sectionToDelete);
    setSaving(true);
    try {
      const response = await catalogService.update(id, { sections: updatedSections });
      if (response.success) {
        fetchCatalogs(); fetchCatalogDetail(id);
        toast.success('تم حذف القسم!');
      } else { toast.error(response.message || 'فشل في حذف القسم'); }
    } catch (err) { toast.error(err.message || 'فشل في حذف القسم'); }
    finally { setSaving(false); setShowDeleteSectionDialog(false); setSectionToDelete(null); }
  };

  const handleOpenDeleteSectionDialog = (catalog, sectionId) => {
    setSelectedCatalog(catalog); setSectionToDelete(sectionId); setShowDeleteSectionDialog(true);
  };

  const toggleSectionExpanded = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const getSortedOffers = (offers) => {
    if (!offers || !Array.isArray(offers)) return [];
    const sorted = [...offers];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      case 'priceHigh':
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'priceLow':
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'areaHigh':
        return sorted.sort((a, b) => (b.area || 0) - (a.area || 0));
      case 'areaLow':
        return sorted.sort((a, b) => (a.area || 0) - (b.area || 0));
      default:
        return sorted;
    }
  };

  const generateSlug = (name) => {
    const arabicMap = {
      '\u0627':'a','\u0623':'a','\u0625':'a','\u0622':'a','\u0628':'b','\u062A':'t','\u062B':'th','\u062C':'j','\u062D':'h','\u062E':'kh',
      '\u062F':'d','\u0630':'th','\u0631':'r','\u0632':'z','\u0633':'s','\u0634':'sh','\u0635':'s','\u0636':'d','\u0637':'t','\u0638':'z',
      '\u0639':'a','\u063A':'gh','\u0641':'f','\u0642':'q','\u0643':'k','\u0644':'l','\u0645':'m','\u0646':'n','\u0647':'h','\u0648':'w',
      '\u064A':'y','\u0649':'a','\u0629':'h','\u0621':'','\u0626':'y','\u0624':'w','\u0644\u0627':'la'
    };
    const transliterated = name.split('').map(c => arabicMap[c] ?? c).join('');
    const slug = transliterated.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '').substring(0, 40);
    return slug || `catalog-${Date.now()}`;
  };

  const handleMoveSection = async (catalog, sectionIndex, direction) => {
    const id = catalog._id || catalog.id;
    const sections = [...(catalogDetail?.sections || catalog.sections || [])];
    const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    [sections[sectionIndex], sections[newIndex]] = [sections[newIndex], sections[sectionIndex]];
    sections.forEach((s, i) => { s.order = i; });
    setSaving(true);
    try {
      const response = await catalogService.update(id, { sections });
      if (response.success) { fetchCatalogs(); fetchCatalogDetail(id); }
    } catch (err) { toast.error('فشل في ترتيب الأقسام'); }
    finally { setSaving(false); }
  };

  const handleOpenSectionFilters = (catalog, section) => {
    const sectionId = section._id || section.id;
    const sType = section.sectionType || 'filter';
    setEditingSectionFilters({ catalogRef: catalog, sectionId, sectionType: sType });
    setSectionFiltersDraft(normalizeCatalogFilters(section.filters || {}));
    setSectionSettingsName(section.name);
    // في الوضع اليدوي: تحميل IDs العروض الحالية
    if (sType === 'manual') {
      const currentIds = (section.offers || []).map((o) => o._id || o.id || o);
      setManualSelectedOfferIds(currentIds);
    } else {
      setManualSelectedOfferIds([]);
    }
    setManualOfferSearch('');
    setShowSectionFiltersModal(true);
  };

  const handleSaveSectionFilters = async () => {
    if (!editingSectionFilters) return;
    const { catalogRef, sectionId, sectionType } = editingSectionFilters;
    const id = catalogRef._id || catalogRef.id;

    // في الوضع اليدوي: استخدم add/remove API بدلاً من تعديل الفلاتر
    if (sectionType === 'manual') {
      setSaving(true);
      try {
        // العروض الحالية في القسم
        const currentSection = (catalogDetail?.sections || []).find((s) => (s._id || s.id) === sectionId);
        const existingIds = (currentSection?.offers || []).map((o) => (o._id || o.id || o).toString());
        const newIds = manualSelectedOfferIds.map((id) => id.toString());

        const toAdd = newIds.filter((i) => !existingIds.includes(i));
        const toRemove = existingIds.filter((i) => !newIds.includes(i));

        if (toAdd.length > 0) {
          await catalogService.addOffers(id, toAdd, sectionId);
        }
        if (toRemove.length > 0) {
          await catalogService.removeOffers(id, toRemove, sectionId);
        }

        // حفظ الاسم عبر update إذا تغير
        if (sectionSettingsName.trim() && sectionSettingsName.trim() !== currentSection?.name) {
          const updatedSections = (catalogDetail?.sections || []).map((s) => {
            if ((s._id || s.id) === sectionId) return { ...s, name: sectionSettingsName.trim() };
            return s;
          });
          await catalogService.update(id, { sections: updatedSections });
        }

        fetchCatalogs();
        await fetchCatalogDetail(id);
        setShowSectionFiltersModal(false);
        setEditingSectionFilters(null);
        setManualCityFilter('');
        setManualTypeFilter('');
        toast.success('تم حفظ العروض اليدوية!');
      } catch (err) { toast.error(err.message || 'فشل في حفظ العروض'); }
      finally { setSaving(false); }
      return;
    }

    const sections = (catalogDetail?.sections || catalogRef.sections || []).map((s) => {
      if ((s._id || s.id) === sectionId) {
        return {
          ...s,
          name: sectionSettingsName.trim() || s.name,
          filters: sanitizeCatalogFilters(sectionFiltersDraft)
        };
      }
      return s;
    });
    setSaving(true);
    try {
      const response = await catalogService.update(id, { sections });
      if (response.success) {
        fetchCatalogs(); fetchCatalogDetail(id);
        setShowSectionFiltersModal(false);
        setEditingSectionFilters(null);
        setManualCityFilter('');
        setManualTypeFilter('');
        toast.success('تم حفظ فلاتر القسم!');
      } else { toast.error(response.message || 'فشل في حفظ فلاتر القسم'); }
    } catch (err) { toast.error(err.message || 'فشل في حفظ فلاتر القسم'); }
    finally { setSaving(false); }
  };

  const hasSectionFilters = (section) => {
    const f = section.filters || {};
    return !!(f.offerType || (Array.isArray(f.propertyType) ? f.propertyType.length : f.propertyType) || (Array.isArray(f.city) ? f.city.length : f.city) || (Array.isArray(f.district) ? f.district.length : f.district) || f.priceMin || f.priceMax || f.areaMin || f.areaMax || f.deedType || f.offerNature || f.rooms);
  };

  const applyFiltersToOffers = (offers, rawFilters) => {
    const filters = normalizeCatalogFilters(rawFilters);
    const num = (v) => {
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };

    return (offers || []).filter((offer) => {
      if (filters.offerType && offer.offerType !== filters.offerType) return false;
      if (filters.propertyType.length > 0 && !filters.propertyType.includes(offer.propertyType || '')) return false;

      if (filters.city.length > 0 && !filters.city.includes(offer.city || '')) return false;
      if (filters.district.length > 0 && !filters.district.includes(offer.district || '')) return false;

      const priceMin = num(filters.priceMin);
      const priceMax = num(filters.priceMax);
      const areaMin = num(filters.areaMin);
      const areaMax = num(filters.areaMax);

      const offerPrice = Number(offer.price || 0);
      const offerArea = Number(offer.area || 0);

      if (priceMin !== null && priceMin > 0 && offerPrice < priceMin) return false;
      if (priceMax !== null && priceMax > 0 && offerPrice > priceMax) return false;
      if (areaMin !== null && areaMin > 0 && offerArea < areaMin) return false;
      if (areaMax !== null && areaMax > 0 && offerArea > areaMax) return false;

      return true;
    });
  };


  const updateCatalogFilters = (catalogState, key, value, subKey = null) => {
    if (subKey) {
      return {
        ...catalogState,
        filters: {
          ...catalogState.filters,
          [key]: {
            ...(catalogState.filters?.[key] || {}),
            [subKey]: value
          }
        }
      };
    }

    return {
      ...catalogState,
      filters: {
        ...catalogState.filters,
        [key]: value
      }
    };
  };

  const renderFiltersEditor = (catalogState, setCatalogState) => {
    const filters = normalizeCatalogFilters(catalogState.filters);
    const filteredPreview = applyFiltersToOffers(offersPool, filters);
    const previewItems = filteredPreview;

    return (
      <div className="form-section">
        <h3>فلاتر الكتالوج <span style={{fontSize:'13px', fontWeight:'normal', color:'var(--text-secondary)'}}>— اختيارية</span></h3>
        <p className="section-desc">بدون فلاتر = يظهر جميع عروضك. الفلاتر تُضيّق النتائج فقط.</p>

        <div className="form-row two-cols">
          <div className="form-group">
            <label>نوع العرض</label>
            <select className="form-control" value={filters.offerType}
              onChange={(e) => setCatalogState((prev) => updateCatalogFilters(prev, 'offerType', e.target.value))}>
              <option value="">الكل</option>
              <option value="sale">بيع</option>
              <option value="rent">إيجار</option>
            </select>
          </div>
          <div className="form-group">
            <label>نوع العقار</label>
            <MultiSelect
              options={[
                { value: 'villa', label: 'فيلا' },
                { value: 'apartment', label: 'شقة' },
                { value: 'land', label: 'أرض' },
                { value: 'building', label: 'عمارة' },
                { value: 'duplex', label: 'دوبلكس' },
                { value: 'floor', label: 'دور' },
                { value: 'commercial', label: 'تجاري' },
                { value: 'farm', label: 'مزرعة' },
                { value: 'rest_house', label: 'استراحة' },
                { value: 'shop', label: 'محل' },
                { value: 'warehouse', label: 'مستودع' },
                { value: 'studio', label: 'استوديو' },
              ]}
              value={filters.propertyType}
              onChange={(val) => setCatalogState((prev) => updateCatalogFilters(prev, 'propertyType', val))}
              placeholder="الكل"
              searchPlaceholder="ابحث عن نوع..."
            />
          </div>
        </div>

        <div className="form-row two-cols">
          <div className="form-group">
            <label>المدينة</label>
            <MultiSelect
              options={locationCities.map(c => ({ value: c.arLabel, label: c.arLabel }))}
              value={filters.city}
              onChange={(val) => { setCatalogState((prev) => updateCatalogFilters(prev, 'city', val)); setCatalogState((prev) => updateCatalogFilters(prev, 'district', [])); }}
              placeholder="الكل"
              searchPlaceholder="ابحث عن مدينة..."
            />
          </div>
          <div className="form-group">
            <label>الحي</label>
            <MultiSelect
              options={
                (filters.city || []).length > 0
                  ? (filters.city || []).flatMap(cityName => {
                      const cityObj = locationCities.find(c => c.arLabel === cityName);
                      if (!cityObj) return [];
                      const allDistricts = [
                        ...(cityObj.unassignedDistricts || []),
                        ...(cityObj.areas || []).flatMap(a => a.districts || [])
                      ];
                      return allDistricts.map(d => ({ value: d.arLabel, label: d.arLabel }));
                    })
                  : locationCities.flatMap(c => {
                      const allDistricts = [
                        ...(c.unassignedDistricts || []),
                        ...(c.areas || []).flatMap(a => a.districts || [])
                      ];
                      return allDistricts.map(d => ({ value: d.arLabel, label: d.arLabel }));
                    })
              }
              value={filters.district}
              onChange={(val) => setCatalogState((prev) => updateCatalogFilters(prev, 'district', val))}
              placeholder="الكل"
              searchPlaceholder="ابحث عن حي..."
            />
          </div>
        </div>

        <div className="form-row two-cols">
          <div className="form-group">
            <label>السعر من</label>
            <input type="number" className="form-control" min="0" value={filters.priceMin}
              onChange={(e) => setCatalogState((prev) => updateCatalogFilters(prev, 'priceMin', e.target.value))}
              placeholder="0" />
          </div>
          <div className="form-group">
            <label>السعر إلى</label>
            <input type="number" className="form-control" min="0" value={filters.priceMax}
              onChange={(e) => setCatalogState((prev) => updateCatalogFilters(prev, 'priceMax', e.target.value))}
              placeholder="0" />
          </div>
        </div>

        <div className="form-row two-cols">
          <div className="form-group">
            <label>المساحة من (م²)</label>
            <input type="number" className="form-control" min="0" value={filters.areaMin}
              onChange={(e) => setCatalogState((prev) => updateCatalogFilters(prev, 'areaMin', e.target.value))}
              placeholder="0" />
          </div>
          <div className="form-group">
            <label>المساحة إلى (م²)</label>
            <input type="number" className="form-control" min="0" value={filters.areaMax}
              onChange={(e) => setCatalogState((prev) => updateCatalogFilters(prev, 'areaMax', e.target.value))}
              placeholder="0" />
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '8px' }}>
          <label>Live View Preview</label>
          {offersPoolLoading ? (
            <div className="section-desc">جاري تحميل العروض...</div>
          ) : (
            <>
              <div className="section-desc" style={{ marginBottom: '10px' }}>
                العروض المطابقة الآن: <strong>{filteredPreview.length}</strong>
              </div>
              {previewItems.length > 0 ? (
                <div className="filters-live-preview">
                  <div className="offers-list">
                    {previewItems.map((offer) => (
                      <div key={offer._id || offer.id} className="offer-list-item">
                        <div className="offer-image-small">
                          {(offer.coverImage || (offer.images && offer.images[0])) ? (
                            <img src={offer.coverImage || offer.images[0]} alt={offer.title || 'offer'} />
                          ) : (
                            <div className="no-image-placeholder">لا توجد صور</div>
                          )}
                        </div>
                        <div className="offer-info">
                          <div className="offer-badges-inline">
                            <span
                              className="type-badge"
                              style={getPropertyTypeColor(offer.propertyType || offer.type)}
                            >
                              {propertyTypes.find((t) => t.value === (offer.propertyType || offer.type))?.label || 'غير محدد'}
                            </span>
                            <span
                              className="status-badge"
                              style={getStatusColor(offer.status)}
                            >
                              {offer.status === 'available' ? 'متاح' :
                                offer.status === 'sold' ? 'مباع' :
                                  offer.status === 'reserved' ? 'محجوز' :
                                    offer.status === 'rented' ? 'مؤجر' : 'متاح'}
                            </span>
                          </div>
                          <h3>{offer.title || 'بدون عنوان'}</h3>
                          <div className="offer-meta">
                            <span><MapPin size={14} /> {offer.city || '-'} - {offer.district || '-'}</span>
                            {Number(offer.area || 0) > 0 && <span><Maximize size={14} /> {formatArea(offer.area)}</span>}
                          </div>
                          <span className="offer-price-inline">
                            {formatPrice(offer.price)}
                            {offer.offerType === 'rent' ? ' / سنوياً' : ''}
                          </span>
                        </div>
                        <div className="offer-price-col">
                          <span className="price">{formatPrice(offer.price)}</span>
                          {offer.offerType === 'rent' && <span className="period">/ سنوياً</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="section-desc">لا توجد عروض مطابقة لهذه الفلاتر حالياً.</div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const getQuickFilterDistrictOptions = (cityName) => {
    const citiesToRead = cityName
      ? locationCities.filter((city) => city.arLabel === cityName)
      : locationCities;

    return citiesToRead.flatMap((city) => {
      const allDistricts = [
        ...(city.unassignedDistricts || []),
        ...(city.areas || []).flatMap((area) => area.districts || [])
      ];
      return allDistricts.map((district) => district.arLabel).filter(Boolean);
    });
  };

  const updateQuickFilter = (index, updater) => {
    setEditingCatalog((prev) => {
      const quickFilters = normalizeQuickFilters(prev.quickFilters);
      const current = quickFilters[index] || createEmptyQuickFilter(index);
      const updated = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      quickFilters[index] = updated;
      return { ...prev, quickFilters };
    });
  };

  const updateQuickFilterField = (index, key, value) => {
    updateQuickFilter(index, (quickFilter) => ({
      ...quickFilter,
      filters: {
        ...(quickFilter.filters || {}),
        [key]: value
      }
    }));
  };

  const addQuickFilter = () => {
    setEditingCatalog((prev) => {
      const quickFilters = normalizeQuickFilters(prev.quickFilters);
      return {
        ...prev,
        quickFilters: [
          createEmptyQuickFilter(0),
          ...quickFilters.map((quickFilter, order) => ({ ...quickFilter, order: order + 1 }))
        ]
      };
    });
    setOpenQuickFilterIndex(0);
  };

  const removeQuickFilter = (index) => {
    setEditingCatalog((prev) => ({
      ...prev,
      quickFilters: normalizeQuickFilters(prev.quickFilters)
        .filter((_, itemIndex) => itemIndex !== index)
        .map((quickFilter, order) => ({ ...quickFilter, order }))
    }));
    setOpenQuickFilterIndex(null);
  };

  const moveQuickFilter = (index, direction) => {
    setEditingCatalog((prev) => {
      const quickFilters = normalizeQuickFilters(prev.quickFilters);
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= quickFilters.length) return prev;
      [quickFilters[index], quickFilters[nextIndex]] = [quickFilters[nextIndex], quickFilters[index]];
      setOpenQuickFilterIndex(nextIndex);
      return {
        ...prev,
        quickFilters: quickFilters.map((quickFilter, order) => ({ ...quickFilter, order }))
      };
    });
  };

  const saveQuickFilterDraft = (index) => {
    const quickFilter = normalizeQuickFilters(editingCatalog.quickFilters)[index];
    if (!quickFilter?.label?.trim()) {
      toast.warning('يرجى إدخال اسم الفلتر الجاهز');
      return;
    }
    if (Object.keys(cleanQuickFilterFilters(quickFilter.filters)).length === 0) {
      toast.warning('اختر شرطًا واحدًا على الأقل للفلتر.');
      return;
    }
    updateQuickFilter(index, {
      label: quickFilter.label.trim(),
      filters: cleanQuickFilterFilters(quickFilter.filters)
    });
    setOpenQuickFilterIndex(null);
  };

  const renderQuickFiltersEditor = () => {
    const quickFilters = normalizeQuickFilters(editingCatalog.quickFilters);

    return (
      <div className="form-section quick-filters-editor">
        <div className="quick-filters-header">
          <div>
            <h3>الفلاتر الجاهزة</h3>
            <p className="section-desc">أنشئ اختصارات تصفح مثل: عمائر الجبيل، فلل الدمام، شقق للإيجار.</p>
            <p className="section-desc">هذه الفلاتر تظهر أعلى الكتالوج العام عندما يكون هذا الكتالوج هو الرئيسي.</p>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={addQuickFilter}>
            <Plus size={16} /> إضافة فلتر جاهز
          </button>
        </div>

        {quickFilters.length === 0 ? (
          <div className="quick-filters-empty">لا توجد فلاتر جاهزة بعد.</div>
        ) : (
          <div className="quick-filters-list">
            {quickFilters.map((quickFilter, index) => {
              const filters = quickFilter.filters || {};
              const districtOptions = getQuickFilterDistrictOptions(filters.city);
              const isOpen = openQuickFilterIndex === index;
              const activeConditionsCount = Object.keys(cleanQuickFilterFilters(filters)).length;

              return (
                <div key={quickFilter._id || quickFilter.id || index} className={`quick-filter-card ${isOpen ? 'open' : ''}`}>
                  <div className="quick-filter-summary">
                    <div className="quick-filter-summary-main">
                      <strong>{quickFilter.label || 'فلتر جاهز جديد'}</strong>
                      <span className={`quick-filter-status ${quickFilter.isActive !== false ? 'active' : 'inactive'}`}>
                        {quickFilter.isActive !== false ? 'مفعل' : 'غير مفعل'}
                      </span>
                      {activeConditionsCount > 0 && (
                        <span className="quick-filter-count">{activeConditionsCount} شرط</span>
                      )}
                    </div>
                    <div className="quick-filter-summary-actions">
                      <button type="button" className="btn-icon" onClick={() => setOpenQuickFilterIndex(isOpen ? null : index)} title="تعديل">
                        <Settings size={15} />
                      </button>
                      <button type="button" className="btn-icon" onClick={() => updateQuickFilter(index, { isActive: quickFilter.isActive === false })} title={quickFilter.isActive !== false ? 'تعطيل' : 'تفعيل'}>
                        <Check size={15} />
                      </button>
                      <button type="button" className="btn-icon" onClick={() => moveQuickFilter(index, 'up')} disabled={index === 0} title="نقل لأعلى">
                        <ChevronUp size={15} />
                      </button>
                      <button type="button" className="btn-icon" onClick={() => moveQuickFilter(index, 'down')} disabled={index === quickFilters.length - 1} title="نقل لأسفل">
                        <ChevronDown size={15} />
                      </button>
                      <button type="button" className="btn-icon danger" onClick={() => removeQuickFilter(index)} title="حذف الفلتر">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <>
                      <div className="quick-filter-card-top">
                        <div className="form-group quick-filter-label">
                          <label>اسم الفلتر</label>
                          <input
                            type="text"
                            className="form-control"
                            value={quickFilter.label}
                            onChange={(e) => updateQuickFilter(index, { label: e.target.value })}
                            placeholder="مثال: عمائر الجبيل"
                          />
                        </div>
                        <label className="quick-filter-active">
                          <input
                            type="checkbox"
                            checked={quickFilter.isActive !== false}
                            onChange={(e) => updateQuickFilter(index, { isActive: e.target.checked })}
                          />
                          <span>{quickFilter.isActive !== false ? 'مفعل' : 'غير مفعل'}</span>
                        </label>
                      </div>

                      <div className="quick-filter-grid">
                        <div className="form-group">
                          <label>نوع العقار</label>
                          <select className="form-control" value={filters.propertyType || ''} onChange={(e) => updateQuickFilterField(index, 'propertyType', e.target.value)}>
                            <option value="">الكل</option>
                            {propertyTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>نوع العرض</label>
                          <select className="form-control" value={filters.offerType || ''} onChange={(e) => updateQuickFilterField(index, 'offerType', e.target.value)}>
                            <option value="">الكل</option>
                            <option value="sale">بيع</option>
                            <option value="rent">إيجار</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>المدينة</label>
                          <select className="form-control" value={filters.city || ''} onChange={(e) => updateQuickFilter(index, (current) => ({ ...current, filters: { ...(current.filters || {}), city: e.target.value, district: '' } }))}>
                            <option value="">كل المدن</option>
                            {locationCities.map((city) => <option key={city.arLabel} value={city.arLabel}>{city.arLabel}</option>)}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>الحي</label>
                          <select className="form-control" value={filters.district || ''} onChange={(e) => updateQuickFilterField(index, 'district', e.target.value)}>
                            <option value="">كل الأحياء</option>
                            {[...new Set(districtOptions)].map((district) => <option key={district} value={district}>{district}</option>)}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>السعر من</label>
                          <input type="number" min="0" className="form-control" value={filters.priceMin || ''} onChange={(e) => updateQuickFilterField(index, 'priceMin', e.target.value)} placeholder="0" />
                        </div>

                        <div className="form-group">
                          <label>السعر إلى</label>
                          <input type="number" min="0" className="form-control" value={filters.priceMax || ''} onChange={(e) => updateQuickFilterField(index, 'priceMax', e.target.value)} placeholder="0" />
                        </div>

                        <div className="form-group">
                          <label>المساحة من</label>
                          <input type="number" min="0" className="form-control" value={filters.areaMin || ''} onChange={(e) => updateQuickFilterField(index, 'areaMin', e.target.value)} placeholder="0" />
                        </div>

                        <div className="form-group">
                          <label>المساحة إلى</label>
                          <input type="number" min="0" className="form-control" value={filters.areaMax || ''} onChange={(e) => updateQuickFilterField(index, 'areaMax', e.target.value)} placeholder="0" />
                        </div>
                      </div>

                      <div className="quick-filter-actions">
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => saveQuickFilterDraft(index)}>
                          <Check size={16} /> حفظ الفلتر
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="catalog-page">
        <div className="loading-container">
          <Loader className="spinner" size={48} />
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="catalog-page">
        <ErrorState error={error} onRetry={fetchCatalogs} />
      </div>
    );
  }

  return (
    <PermissionGate feature="PAGE_CATALOG" type="page">
    <div className="catalog-page">
      <div className="page-header">
        <div className="header-title">
          <Share2 size={28} />
          <h1>{t('catalog.title')}</h1>
          <span className="count">{catalogs.filter((c) => !c.isDefault).length} كتالوج</span>
          <LimitBadge limitCode="LIMIT_CATALOGS" currentCount={catalogs.filter((c) => !c.isDefault).length} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-outline"
              onClick={() => setShowCatalogSortMenu((v) => !v)}
              title="ترتيب الكتالوجات"
            >
              <ArrowUpDown size={16} />
              {catalogListSort === 'newest' ? 'الأحدث' : catalogListSort === 'oldest' ? 'الأقدم' : catalogListSort === 'nameAZ' ? 'أ-ي' : 'ي-أ'}
            </button>
            {showCatalogSortMenu && (
              <div style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--surface-color)', border: '1px solid var(--border-color)', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: '140px', overflow: 'hidden' }}>
                {[
                  { value: 'newest', label: 'الأحدث أولاً' },
                  { value: 'oldest', label: 'الأقدم أولاً' },
                  { value: 'nameAZ', label: 'الاسم أ-ي' },
                  { value: 'nameZA', label: 'الاسم ي-أ' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'right', background: catalogListSort === opt.value ? 'var(--primary-light, #e8f4ff)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary)' }}
                    onClick={() => { setCatalogListSort(opt.value); setShowCatalogSortMenu(false); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-primary" data-tour="create-catalog-btn" onClick={() => setShowCreateModal(true)} disabled={saving}>
            <Plus size={20} />
            {t('catalog.createCatalog')}
          </button>
        </div>
      </div>

      <div className="catalog-intro">
        <div className="intro-content">
          <h3>شارك عروضك مع العملاء بطريقة احترافية</h3>
          <p>الكتالوج الرئيسي يعرض كل العروض تلقائياً، وباقي الكتالوجات تعمل كـ Views ديناميكية حسب الفلاتر.</p>
        </div>
        <div className="intro-steps">
          {CATALOG_STEPS.map((step, i) => (
            <React.Fragment key={step.key}>
              <div className="step-btn-wrap">
                <button
                  className={`step-btn${activeStepTooltip === step.key ? ' active' : ''}`}
                  onClick={() => setActiveStepTooltip(activeStepTooltip === step.key ? null : step.key)}
                  onMouseEnter={() => setActiveStepTooltip(step.key)}
                  onMouseLeave={() => setActiveStepTooltip(null)}
                >
                  {step.label}
                </button>
                {activeStepTooltip === step.key && (
                  <div className="step-tooltip">{step.tooltip}</div>
                )}
              </div>
              {i < CATALOG_STEPS.length - 1 && (
                <ChevronLeft size={14} className="step-arrow" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ───── view تفاصيل الكتالوج ───── */}
      {selectedCatalogView && (
        <div className="catalog-detail-view">
          {/* شريط الرجوع */}
          <div className="catalog-detail-topbar">
            <button className="btn-back" onClick={handleCloseCatalogView}>
              <ChevronRight size={20} /> رجوع
            </button>
            <div className="catalog-detail-title">
              <span className="catalog-detail-color-dot" style={{ background: themes.find(t => t.id === selectedCatalogView.theme)?.color }} />
              <h2>{selectedCatalogView.name}</h2>
              {selectedCatalogView.isDefault && <span className="badge badge-theme">رئيسي</span>}
            </div>
            <div className="catalog-detail-actions">
              <button className="action-btn" onClick={() => handleOpenCatalogLink(selectedCatalogView)} title="فتح الكتالوج العام"><ExternalLink size={16} /></button>
              <button className="action-btn" onClick={() => handleShareCatalog(selectedCatalogView)} title="مشاركة"><Share2 size={16} /></button>
              <button className="action-btn" onClick={() => handleEditCatalog(selectedCatalogView)} title="تعديل"><Settings size={16} /></button>
              {!selectedCatalogView.isDefault && (
                <button className="action-btn danger" onClick={() => handleOpenDeleteDialog(selectedCatalogView)} title="حذف" disabled={saving}><Trash2 size={16} /></button>
              )}
            </div>
          </div>

          {/* محتوى التفاصيل */}
          {detailLoading ? (
            <div className="sections-loading">
              <Loader className="spinner" size={32} />
              <p>جاري التحميل...</p>
            </div>
          ) : (
            <>
              <div className="sections-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h4><Layers size={20} />{selectedCatalogView.isDefault ? 'عروض الكتالوج الافتراضي' : 'أقسام الكتالوج'}</h4>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--surface-color)', color: 'var(--text-primary)' }}>
                    <option value="newest">الأحدث أولاً</option>
                    <option value="oldest">الأقدم أولاً</option>
                    <option value="priceHigh">الأعلى سعراً</option>
                    <option value="priceLow">الأقل سعراً</option>
                    <option value="areaHigh">الأكبر مساحة</option>
                    <option value="areaLow">الأصغر مساحة</option>
                  </select>
                  <button className="btn-icon" title={adminViewMode === 'list' ? 'عرض معرض الصور' : 'عرض القائمة'}
                    onClick={() => setAdminViewMode(v => v === 'list' ? 'gallery' : 'list')}>
                    {adminViewMode === 'list' ? <Grid size={16} /> : <List size={16} />}
                  </button>
                </div>
                {!selectedCatalogView.isDefault && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setSelectedCatalog(selectedCatalogView); setShowAddSectionModal(true); }}>
                    <FolderPlus size={16} />إضافة قسم
                  </button>
                )}
              </div>

              {/* العروض العامة — مخفية افتراضياً */}
              {catalogDetail && catalogDetail.offers && catalogDetail.offers.length > 0 && (
                <div className="section-block">
                  <div className="section-block-header" onClick={() => toggleSectionExpanded('general')}>
                    <div className="section-title-row">
                      <span className="section-icon">📋</span>
                      <h5>عروض عامة</h5>
                      <span className="section-count">{catalogDetail.offers.length} عرض</span>
                    </div>
                    {expandedSections['general'] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                  {expandedSections['general'] && (
                    <div className={adminViewMode === 'gallery' ? 'section-offers-gallery' : 'section-offers'}>
                      {getSortedOffers(catalogDetail.offers).map(offer => (
                        <div key={offer._id || offer.id} className={adminViewMode === 'gallery' ? 'admin-offer-gallery-card' : 'section-offer-item'}>
                          <div className="offer-thumb" onClick={() => handleOpenCatalogOfferLink(selectedCatalogView, offer)}>
                            {(offer.coverImage || (offer.images && offer.images[0])) ? (
                              <img src={offer.coverImage || offer.images[0]} alt={offer.title} />
                            ) : (
                              <div className="offer-thumb-placeholder">
                                <img
                                  src={user?.avatar || '/mesk-brand-icon.svg'}
                                  alt=""
                                  className={user?.avatar ? 'offer-thumb-bg' : 'offer-thumb-logo'}
                                />
                                <span className="no-img-text">لا توجد صورة</span>
                              </div>
                            )}
                          </div>
                          <div className="offer-brief" onClick={() => handleOpenCatalogOfferLink(selectedCatalogView, offer)}>
                            <span className="offer-title">{offer.title}</span>
                            <span className="offer-info">
                              {offer.city && <span><MapPin size={12} /> {offer.city}</span>}
                              {offer.price > 0 && <span><DollarSign size={12} /> {(offer.price || 0).toLocaleString()} ر.س</span>}
                              {offer.area > 0 && <span><Maximize size={12} /> {offer.area} م²</span>}
                            </span>
                          </div>
                          <button className="offer-copy-link-btn" title="نسخ رابط العرض"
                            onClick={async (e) => { e.stopPropagation(); const link = getCatalogOfferShareUrl(selectedCatalogView, offer); await copyToClipboard(link); toast.success('تم نسخ رابط العرض!'); }}>
                            <Copy size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* الأقسام — مخفية افتراضياً */}
              {!selectedCatalogView.isDefault && catalogDetail && catalogDetail.sections && catalogDetail.sections.length > 0 ? (
                catalogDetail.sections.map((section, sectionIndex) => {
                  const sectionId = section._id || section.id;
                  return (
                    <div key={sectionId} className="section-block">
                      <div className="section-block-header" onClick={() => toggleSectionExpanded(sectionId)}>
                        <div className="section-title-row">
                          <GripVertical size={16} className="drag-handle" />
                          <span className="section-icon">📁</span>
                          <h5>{section.name}</h5>
                          {(section.sectionType || 'filter') === 'manual' && (
                            <span className="badge-manual-section">✋ يدوي</span>
                          )}
                          <span className="section-count">{section.offers?.length || 0} عرض</span>
                        </div>
                        <div className="section-actions" onClick={(e) => e.stopPropagation()}>
                          <button className={`btn-icon${hasSectionFilters(section) ? ' active-filter' : ''}`}
                            onClick={() => handleOpenSectionFilters(selectedCatalogView, section)} title="إعدادات القسم">
                            <Settings size={14} />
                          </button>
                          {sectionIndex > 0 && <button className="btn-icon" onClick={() => handleMoveSection(selectedCatalogView, sectionIndex, 'up')} title="نقل لأعلى"><ChevronUp size={14} /></button>}
                          {sectionIndex < (catalogDetail.sections.length - 1) && <button className="btn-icon" onClick={() => handleMoveSection(selectedCatalogView, sectionIndex, 'down')} title="نقل لأسفل"><ChevronDown size={14} /></button>}
                          <button className="btn-icon danger" onClick={() => handleOpenDeleteSectionDialog(selectedCatalogView, sectionId)} title="حذف القسم"><Trash2 size={14} /></button>
                          <button className="btn-icon" title="إضافة قسم فرعي" onClick={(e) => { e.stopPropagation(); setSelectedCatalog(selectedCatalogView); setParentSectionId(sectionId); setShowAddSectionModal(true); }}>＋</button>
                          {expandedSections[sectionId] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </div>
                      {/* أقسام فرعية */}
                      {section.subSections && section.subSections.length > 0 && (
                        <div className="sub-sections-list">
                          {section.subSections.map((sub, subIdx) => (
                            <div key={sub._id || subIdx} className="sub-section-item">
                              <span className="sub-section-icon">📂</span>
                              <span className="sub-section-name">{sub.name}</span>
                              <span className="section-count">{sub.offers?.length || 0} عرض</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {expandedSections[sectionId] && (
                        <div className={adminViewMode === 'gallery' ? 'section-offers-gallery' : 'section-offers'}>
                          {section.offers && section.offers.length > 0 ? (
                            getSortedOffers(section.offers).map(offer => (
                              <div key={offer._id || offer.id} className={adminViewMode === 'gallery' ? 'admin-offer-gallery-card' : 'section-offer-item'}>
                                <div className="offer-thumb" onClick={() => handleOpenCatalogOfferLink(selectedCatalogView, offer)}>
                                  {(offer.coverImage || (offer.images && offer.images[0])) ? (
                                    <img src={offer.coverImage || offer.images[0]} alt={offer.title} />
                                  ) : (
                                    <div className="offer-thumb-placeholder">
                                      <img
                                        src={user?.avatar || '/mesk-brand-icon.svg'}
                                        alt=""
                                        className={user?.avatar ? 'offer-thumb-bg' : 'offer-thumb-logo'}
                                      />
                                      <span className="no-img-text">لا توجد صورة</span>
                                    </div>
                                  )}
                                </div>
                                <div className="offer-brief" onClick={() => handleOpenCatalogOfferLink(selectedCatalogView, offer)}>
                                  <span className="offer-title">{offer.title}</span>
                                  <span className="offer-info">
                                    {offer.city && <span><MapPin size={12} /> {offer.city}</span>}
                                    {offer.price > 0 && <span><DollarSign size={12} /> {(offer.price || 0).toLocaleString()} ر.س</span>}
                                    {offer.area > 0 && <span><Maximize size={12} /> {offer.area} م²</span>}
                                  </span>
                                </div>
                                <button className="offer-copy-link-btn" title="نسخ رابط العرض"
                                  onClick={async (e) => { e.stopPropagation(); const link = getCatalogOfferShareUrl(selectedCatalogView, offer); await copyToClipboard(link); toast.success('تم نسخ رابط العرض!'); }}>
                                  <Copy size={13} />
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="section-empty">
                              <p>{(section.sectionType || 'filter') === 'manual' ? 'لا توجد عروض — اختر عروضاً من إعدادات القسم' : 'لا توجد عروض — حدّد فلاتر القسم لتظهر هنا تلقائياً'}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                !selectedCatalogView.isDefault && !catalogDetail?.offers?.length && (
                  <div className="sections-empty-state">
                    <Layers size={48} />
                    <h4>لا توجد أقسام بعد</h4>
                    <p>أضف أقسام لتنظيم عروضك داخل الكتالوج</p>
                    <button className="btn btn-primary btn-sm" onClick={() => { setSelectedCatalog(selectedCatalogView); setShowAddSectionModal(true); }}>
                      <FolderPlus size={16} />إضافة قسم جديد
                    </button>
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}

      {/* ───── قائمة الكتالوجات (صفوف بسيطة) ───── */}
      {!selectedCatalogView && (
      <>
      {catalogs.length === 0 ? (
        <div className="empty-state">
          <Share2 size={64} />
          <h3>{t('catalog.noCatalogs')}</h3>
          <p>أنشئ كتالوجك الأول وابدأ بمشاركة عروضك</p>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={20} />
            {t('catalog.createCatalog')}
          </button>
        </div>
      ) : (
        <div className="catalogs-list">
          {[...catalogs].sort((a, b) => {
            // FIX 5: default catalog always pinned first, regardless of sort order
            if (a.isDefault && !b.isDefault) return -1;
            if (!a.isDefault && b.isDefault) return 1;
            if (catalogListSort === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
            if (catalogListSort === 'nameAZ') return (a.name || '').localeCompare(b.name || '', 'ar');
            if (catalogListSort === 'nameZA') return (b.name || '').localeCompare(a.name || '', 'ar');
            return new Date(b.createdAt) - new Date(a.createdAt);
          }).map(catalog => {
            const catalogId = catalog._id || catalog.id;
            const isMenuOpen = openMenuId === catalogId;
            return (
              <div key={catalogId} className="catalog-row">
                {/* صورة مصغرة */}
                {catalog.catalogThumbnail ? (
                  <img src={catalog.catalogThumbnail} className="catalog-thumbnail" alt="" />
                ) : (
                  <div className="catalog-thumbnail-placeholder"><Home size={22} /></div>
                )}
                {/* شريط اللون */}
                <div className="catalog-row-color" style={{ background: themes.find(t => t.id === catalog.theme)?.color }} />
                {/* الاسم والمعلومات — قابل للنقر */}
                <div className="catalog-row-info" onClick={() => handleOpenCatalogView(catalog)}>
                  <span className="catalog-row-name">
                    {catalog.name}
                    {/* FIX 5: رئيسي badge + pin indicator for default catalog */}
                    {catalog.isDefault && (
                      <>
                        <span className="badge badge-theme" style={{marginRight:'8px',fontSize:'11px'}}>رئيسي</span>
                        <span style={{marginRight:'4px',fontSize:'11px',color:'var(--text-secondary)'}}>📌 مثبت</span>
                      </>
                    )}
                  </span>
                  <span className="catalog-row-meta">
                    <span><Home size={13} /> {catalog.offersCount || catalog.offers?.length || 0} عرض</span>
                    <span><Layers size={13} /> {catalog.sectionsCount || catalog.sections?.length || 0} أقسام</span>
                    <span><Eye size={13} /> {catalog.viewsCount || 0} مشاهدة</span>
                  </span>
                </div>
                {/* زر 3 نقاط */}
                <div style={{ position: 'relative' }}>
                  <button className="catalog-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      // FIX 4: check if button is in bottom half of screen; if so open upward
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenuUpward(rect.bottom > window.innerHeight / 2);
                      setOpenMenuId(isMenuOpen ? null : catalogId);
                    }}
                    title="خيارات">
                    ⋮
                  </button>
                  {isMenuOpen && (
                    <div
                      className="catalog-menu-dropdown"
                      onClick={(e) => e.stopPropagation()}
                      style={menuUpward ? { bottom: '100%', top: 'auto', marginBottom: '4px' } : {}}
                    >
                      <button onClick={() => { handleOpenCatalogView(catalog); setOpenMenuId(null); }}><Eye size={15} /> فتح التفاصيل</button>
                      <button onClick={() => { handleEditCatalog(catalog); setOpenMenuId(null); }}><Settings size={15} /> تعديل</button>
                      <button data-tour="catalog-share" onClick={() => { handleShareCatalog(catalog); setOpenMenuId(null); }}><Share2 size={15} /> مشاركة</button>
                      <button onClick={() => { handleOpenCatalogLink(catalog); setOpenMenuId(null); }}><ExternalLink size={15} /> فتح الكتالوج</button>
                      {!catalog.isDefault && (
                        <button className="danger" onClick={() => { handleOpenDeleteDialog(catalog); setOpenMenuId(null); }} disabled={saving}><Trash2 size={15} /> حذف</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {catalogPagination.total > 1 && (
        <GooglePagination
          currentPage={catalogPagination.current}
          totalPages={catalogPagination.total}
          onPageChange={(page) => setCatalogPagination((prev) => ({ ...prev, current: page }))}
          prevLabel={t('common.previous')}
          nextLabel={t('common.next')}
          infoPrefix={t('common.page')}
          infoMiddle={t('common.of')}
          isRTL={true}
        />
      )}

      <ConfirmDialog isOpen={showDeleteDialog}
        onClose={() => { setShowDeleteDialog(false); setCatalogToDelete(null); }}
        onConfirm={handleConfirmDeleteCatalog}
        title="حذف الكتالوج"
        message={`هل أنت متأكد من حذف الكتالوج "${catalogToDelete?.name}"؟ سيتم حذف جميع الأقسام والعروض المرتبطة.`}
        confirmText="حذف" cancelText="إلغاء" type="danger" loading={saving} />

      <ConfirmDialog isOpen={showDeleteSectionDialog}
        onClose={() => { setShowDeleteSectionDialog(false); setSectionToDelete(null); }}
        onConfirm={handleDeleteSection}
        title="حذف القسم"
        message="هل أنت متأكد من حذف هذا القسم؟ ستبقى العروض في الكتالوج."
        confirmText="حذف" cancelText="إلغاء" type="danger" loading={saving} />

      {showAddSectionModal && selectedCatalog && (
        <div className="modal-overlay" onClick={() => { setShowAddSectionModal(false); setNewSectionStep(1); setNewSectionName(''); setNewSectionType('filter'); setNewSectionFilters({ ...DEFAULT_CATALOG_FILTERS }); setParentSectionId(null); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FolderPlus size={24} />{parentSectionId ? 'إضافة قسم فرعي' : 'إضافة قسم جديد'}</h2>
              <button className="modal-close" onClick={() => { setShowAddSectionModal(false); setNewSectionStep(1); setNewSectionName(''); setNewSectionType('filter'); setNewSectionFilters({ ...DEFAULT_CATALOG_FILTERS }); setParentSectionId(null); }}><X size={24} /></button>
            </div>

            {/* مؤشر الخطوات — الخطوة الثانية تظهر فقط للفلاتر */}
            <div className="create-steps-bar">
              {[{ label: 'الإعداد' }, ...(newSectionType === 'filter' ? [{ label: 'الفلاتر' }] : [])].map((step, i) => (
                <div key={i} className={`step-item ${newSectionStep > i + 1 ? 'done' : ''} ${newSectionStep === i + 1 ? 'active' : ''}`}>
                  <div className="step-dot">{newSectionStep > i + 1 ? <Check size={12} /> : i + 1}</div>
                  <span className="step-label">{step.label}</span>
                  {newSectionType === 'filter' && i < 1 && <div className="step-line" />}
                </div>
              ))}
            </div>

            <div className="modal-body">
              {newSectionStep === 1 && (
                <>
                  <div className="form-group">
                    <label>اسم القسم <span className="required">*</span></label>
                    <input type="text" className="form-control" value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      placeholder="مثال: عروض الفلل، أراضي سكنية..."
                      onKeyDown={(e) => { if (e.key === 'Enter' && newSectionName.trim() && newSectionType === 'filter') setNewSectionStep(2); }}
                      autoFocus />
                  </div>
                  {/* اختيار نوع القسم */}
                  <div className="section-type-choice">
                    <label className="section-type-label">طريقة إضافة العروض</label>
                    <div className="section-type-options">
                      <label className={`section-type-option ${newSectionType === 'filter' ? 'selected' : ''}`}>
                        <input type="radio" name="sectionType" value="filter"
                          checked={newSectionType === 'filter'}
                          onChange={() => setNewSectionType('filter')} />
                        <div className="section-type-content">
                          <span className="section-type-icon">🔄</span>
                          <div>
                            <strong>تلقائي بالفلاتر</strong>
                            <p>العروض تُضاف وتُحذف تلقائياً حسب الفلاتر</p>
                          </div>
                        </div>
                      </label>
                      <label className={`section-type-option ${newSectionType === 'manual' ? 'selected' : ''}`}>
                        <input type="radio" name="sectionType" value="manual"
                          checked={newSectionType === 'manual'}
                          onChange={() => setNewSectionType('manual')} />
                        <div className="section-type-content">
                          <span className="section-type-icon">✋</span>
                          <div>
                            <strong>يدوي</strong>
                            <p>أنت تختار العروض التي تظهر في هذا القسم</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </>
              )}
              {newSectionStep === 2 && newSectionType === 'filter' && renderFiltersEditor(
                { filters: newSectionFilters },
                (updater) => {
                  setNewSectionFilters((prev) => {
                    const next = typeof updater === 'function' ? updater({ filters: prev }) : updater;
                    return next.filters || prev;
                  });
                }
              )}
            </div>

            <div className="modal-footer">
              {newSectionStep === 1 ? (
                <>
                  <button className="btn btn-outline" onClick={() => { setShowAddSectionModal(false); setNewSectionName(''); setNewSectionType('filter'); setNewSectionFilters({ ...DEFAULT_CATALOG_FILTERS }); setParentSectionId(null); }}>إلغاء</button>
                  {newSectionType === 'filter' ? (
                    <button className="btn btn-primary" onClick={() => setNewSectionStep(2)} disabled={!newSectionName.trim()}>
                      التالي — إعداد الفلاتر
                    </button>
                  ) : (
                    <button className="btn btn-primary" onClick={() => handleAddSection(selectedCatalog)} disabled={saving || !newSectionName.trim()}>
                      {saving ? <Loader className="spinner" size={18} /> : <Check size={18} />}
                      إنشاء القسم
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button className="btn btn-outline" onClick={() => setNewSectionStep(1)}>السابق</button>
                  <button className="btn btn-outline" onClick={() => handleAddSection(selectedCatalog)} disabled={saving}>
                    تخطي وحفظ
                  </button>
                  <button className="btn btn-primary" onClick={() => handleAddSection(selectedCatalog)} disabled={saving}>
                    {saving ? <Loader className="spinner" size={18} /> : <Check size={18} />}
                    إنشاء القسم
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setCreateStep(1); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Plus size={24} />{t('catalog.createCatalog')}</h2>
              <button className="modal-close" onClick={() => { setShowCreateModal(false); setCreateStep(1); }}><X size={24} /></button>
            </div>

            {/* مؤشر الخطوات */}
            <div className="create-steps-indicator">
              {[{ n: 1, label: 'المعلومات' }, { n: 2, label: 'الفلاتر' }, { n: 3, label: 'المراجعة' }].map(({ n, label }) => (
                <div key={n} className={`create-step-dot ${createStep === n ? 'active' : ''} ${createStep > n ? 'done' : ''}`}>
                  <span className="dot-num">{createStep > n ? '✓' : n}</span>
                  <span className="dot-label">{label}</span>
                </div>
              ))}
            </div>

            <div className="modal-body">
              {/* الخطوة ١ — المعلومات الأساسية */}
              {createStep === 1 && (
                <>
                  <div className="form-section">
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('catalog.catalogName')} <span className="required">*</span></label>
                        <input type="text" className="form-control" value={newCatalog.name}
                          onChange={(e) => { setNewCatalog({ ...newCatalog, name: e.target.value, slug: generateSlug(e.target.value) }); }}
                          placeholder="مثال: كتالوج فلل الرياض" autoFocus />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>الوصف <span style={{fontSize:'12px', color:'var(--text-secondary)'}}>— اختياري</span></label>
                        <textarea className="form-control" rows={2} value={newCatalog.description}
                          onChange={(e) => setNewCatalog({ ...newCatalog, description: e.target.value })}
                          placeholder="وصف مختصر للكتالوج..." />
                      </div>
                    </div>
                  </div>
                  <div className="form-section">
                    <h3><Palette size={18} /> المظهر</h3>
                    <select className="form-control" value={newCatalog.theme}
                      onChange={(e) => setNewCatalog({ ...newCatalog, theme: e.target.value })}>
                      {themes.map(theme => (
                        <option key={theme.id} value={theme.id}>{theme.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* الخطوة ٢ — الفلاتر */}
              {createStep === 2 && renderFiltersEditor(newCatalog, setNewCatalog)}

              {/* الخطوة ٣ — المراجعة */}
              {createStep === 3 && (
                <div className="form-section">
                  <h3>مراجعة قبل الإنشاء</h3>
                  <div className="review-summary">
                    <div className="review-row">
                      <span className="review-label">الاسم</span>
                      <span className="review-value">{newCatalog.name}</span>
                    </div>
                    {newCatalog.description && (
                      <div className="review-row">
                        <span className="review-label">الوصف</span>
                        <span className="review-value">{newCatalog.description}</span>
                      </div>
                    )}
                    <div className="review-row">
                      <span className="review-label">المظهر</span>
                      <span className="review-value">{themes.find(t => t.id === newCatalog.theme)?.name || 'الافتراضي'}</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">الرابط</span>
                      <span className="review-value" style={{direction:'ltr', fontSize:'13px'}}>msk.sa/c/{newCatalog.slug || '...'}</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">العروض المطابقة</span>
                      <span className="review-value">{applyFiltersToOffers(offersPool, newCatalog.filters).length} عرض</span>
                    </div>
                  </div>
                  <div className="form-row" style={{marginTop:'16px'}}>
                    <div className="form-group">
                      <label>رابط مخصص <span style={{fontSize:'12px', color:'var(--text-secondary)'}}>— اختياري</span></label>
                      <div className="link-input">
                        <span className="link-prefix">msk.sa/c/</span>
                        <input type="text" value={newCatalog.slug}
                          onChange={(e) => setNewCatalog({ ...newCatalog, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                          placeholder="my-catalog" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {createStep > 1 && (
                <button className="btn btn-outline" onClick={() => setCreateStep(createStep - 1)}>السابق</button>
              )}
              {createStep < 3 && (
                <button className="btn btn-primary" onClick={() => {
                  if (createStep === 1 && !newCatalog.name) { toast.warning('يرجى إدخال اسم الكتالوج'); return; }
                  setCreateStep(createStep + 1);
                }}>
                  التالي
                </button>
              )}
              {createStep === 3 && (
                <button className="btn btn-primary" onClick={handleCreateCatalog} disabled={saving || !newCatalog.name}>
                  {saving ? <Loader className="spinner" size={18} /> : <Check size={18} />}
                  إنشاء الكتالوج
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingCatalog && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Settings size={24} />تعديل الكتالوج</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}><X size={24} /></button>
            </div>

            {/* مؤشر خطوات التعديل — قابل للنقر */}
            <div className="step-indicator" style={{ flexShrink: 0, background: 'var(--surface-color)' }}>
              {[
                { num: '١', label: 'المعلومات' },
                { num: '٢', label: 'الفلاتر' },
                { num: '٣', label: 'الحقول' },
              ].map((step, index) => {
                const stepNum = index + 1;
                return (
                  <div
                    key={index}
                    className={`step-item ${editCatalogStep === stepNum ? 'active' : ''} ${editCatalogStep > stepNum ? 'done' : ''} clickable`}
                    onClick={() => setEditCatalogStep(stepNum)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="step-dot">{step.num}</span>
                    <span className="step-label">{step.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="modal-body">

              {/* ===== STEP 1: المعلومات الأساسية ===== */}
              {editCatalogStep === 1 && (
              <>
              <div className="form-section">
                <h3>معلومات الكتالوج</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('catalog.catalogName')}</label>
                    <input type="text" className="form-control" value={editingCatalog.name}
                      disabled={editingCatalog.isDefault}
                      onChange={(e) => setEditingCatalog({ ...editingCatalog, name: e.target.value })} />
                    {editingCatalog.isDefault && (
                      <p className="section-desc" style={{ marginTop: '8px' }}>اسم الكتالوج الافتراضي ثابت حتى يبقى رابط كل العروض واضحًا.</p>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>الوصف</label>
                    <textarea className="form-control" rows={2} value={editingCatalog.description}
                      onChange={(e) => setEditingCatalog({ ...editingCatalog, description: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('catalog.customLink')}</label>
                    <div className="link-input">
                      <span className="link-prefix">msk.sa/c/</span>
                      <input type="text" value={editingCatalog.slug}
                        onChange={(e) => {
                          const cleaned = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
                          setEditingCatalog({ ...editingCatalog, slug: cleaned });
                        }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-section">
                <h3><Palette size={20} />{t('catalog.theme')}</h3>
                <select className="form-control" value={editingCatalog.theme}
                  onChange={(e) => setEditingCatalog({ ...editingCatalog, theme: e.target.value })}>
                  {themes.map(theme => (
                    <option key={theme.id} value={theme.id}>{theme.name}</option>
                  ))}
                </select>
              </div>
              </>
              )}

              {/* ===== STEP 2: الفلاتر ===== */}
              {editCatalogStep === 2 && editingCatalog.isDefault && renderQuickFiltersEditor()}

              {editCatalogStep === 2 && !editingCatalog.isDefault && (
                <>
                  {renderFiltersEditor(editingCatalog, setEditingCatalog)}
                  {renderQuickFiltersEditor()}
                </>
              )}

              {/* ===== STEP 3: الحقول الظاهرة ===== */}
              {editCatalogStep === 3 && (
              <div className="form-section">
                <h3>الحقول الظاهرة للزوار</h3>
                <p className="section-desc" style={{marginBottom:'12px'}}>اختر ما يراه الزائر عند فتح أي عرض في الكتالوج.</p>
                <div className="catalog-fields-toggles">
                  {[
                    { key: 'price',       label: 'السعر' },
                    { key: 'area',        label: 'المساحة' },
                    { key: 'rooms',       label: 'الغرف' },
                    { key: 'location',    label: 'الموقع' },
                    { key: 'description', label: 'الوصف' },
                    { key: 'offerNature', label: 'صفة العرض' },
                    { key: 'status',      label: 'الحالة' },
                    { key: 'contact',     label: 'معلومات التواصل' },
                  ].map(({ key, label }) => (
                    <label key={key} className="catalog-field-toggle">
                      <input
                        type="checkbox"
                        checked={editingCatalog.fields?.[key] !== false}
                        onChange={(e) => setEditingCatalog({
                          ...editingCatalog,
                          fields: { ...normalizeCatalogFields(editingCatalog.fields), [key]: e.target.checked }
                        })}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              )}

            </div>

            {/* أزرار التنقل */}
            <div className="modal-footer step-footer">
              <div className="step-nav-buttons">
                {editCatalogStep > 1 ? (
                  <button className="btn btn-outline step-btn-prev" onClick={() => setEditCatalogStep(s => s - 1)}>السابق</button>
                ) : (
                  <button className="btn btn-outline step-btn-prev" onClick={() => setShowEditModal(false)}>إلغاء</button>
                )}
                {editCatalogStep < 3 && (
                  <button
                    className="btn btn-secondary step-btn-next"
                    onClick={() => {
                      if (editCatalogStep === 1 && !editingCatalog.name) { toast.warning('يرجى إدخال اسم الكتالوج'); return; }
                      setEditCatalogStep(s => s + 1);
                    }}
                  >
                    التالي
                  </button>
                )}
                {editCatalogStep >= 2 && (
                  <button data-tour="catalog-save" className="btn btn-primary step-btn-save" onClick={handleSaveEdit} disabled={saving}>
                    {saving ? <><Loader className="spinner" size={16} /> جاري الحفظ...</> : 'حفظ'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showShareModal && selectedCatalog && (
        <div className="modal-overlay" onClick={handleCloseShareModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Share2 size={24} />مشاركة الرابط</h2>
              <button className="modal-close" onClick={handleCloseShareModal}><X size={24} /></button>
            </div>
            <div className="modal-body">
              <div className="share-link-section">
                <label>رابط الكتالوج</label>
                <div className="share-link-box">
                  <code>{window.location.origin}/c/{selectedCatalog.slug}</code>
                  <button className={`copy-btn ${copiedLink === (selectedCatalog._id || selectedCatalog.id) ? 'copied' : ''}`}
                    onClick={() => handleCopyLink(selectedCatalog)}>
                    {copiedLink === (selectedCatalog._id || selectedCatalog.id) ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              {/* زر فتح لوحة الفلتر المخصص */}
              <div style={{ margin: '12px 0' }}>
                <button
                  className="btn btn-outline"
                  style={{ width: '100%', justifyContent: 'center', gap: '8px' }}
                  onClick={() => setShowFilterShare(v => !v)}
                >
                  🎯 شارك مع فلتر مخصص
                </button>
              </div>

              {/* لوحة الفلتر المخصص */}
              {showFilterShare && (
                <div className="share-filter-panel" style={{ background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '10px', padding: '14px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '4px', display: 'block' }}>نوع العقار</label>
                    <select className="form-control" value={shareFilters.propertyType || ''} onChange={e => setShareFilters(p => ({ ...p, propertyType: e.target.value }))}>
                      <option value="">الكل</option>
                      <option value="villa">فيلا</option>
                      <option value="apartment">شقة</option>
                      <option value="land">أرض</option>
                      <option value="office">مكتب</option>
                      <option value="warehouse">مستودع</option>
                      <option value="building">عمارة</option>
                      <option value="floor">دور</option>
                      <option value="duplex">دوبلكس</option>
                      <option value="compound">مجمع سكني</option>
                      <option value="commercial">تجاري</option>
                      <option value="farm">مزرعة</option>
                      <option value="store">محل تجاري</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '4px', display: 'block' }}>نوع العرض</label>
                    <select className="form-control" value={shareFilters.offerType || ''} onChange={e => setShareFilters(p => ({ ...p, offerType: e.target.value }))}>
                      <option value="">الكل</option>
                      <option value="sale">للبيع</option>
                      <option value="rent">للإيجار</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '4px', display: 'block' }}>المدينة</label>
                    <select className="form-control" value={shareFilters.city || ''} onChange={e => setShareFilters(p => ({ ...p, city: e.target.value }))}>
                      <option value="">كل المدن</option>
                      {locationCities.map(c => <option key={c._id || c.value} value={c.arLabel || c.value}>{c.arLabel || c.label || c.value}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px', marginBottom: '4px', display: 'block' }}>الحي (اختياري)</label>
                    <input className="form-control" type="text" placeholder="اكتب اسم الحي" value={shareFilters.district || ''} onChange={e => setShareFilters(p => ({ ...p, district: e.target.value }))} />
                  </div>
                  {/* عرض الرابط المبني */}
                  {(() => {
                    const params = new URLSearchParams();
                    if (shareFilters.propertyType) params.set('propertyType', shareFilters.propertyType);
                    if (shareFilters.offerType)    params.set('offerType',    shareFilters.offerType);
                    if (shareFilters.city)          params.set('city',          shareFilters.city);
                    if (shareFilters.district)      params.set('district',      shareFilters.district);
                    const qs = params.toString();
                    const filteredUrl = `${window.location.origin}/c/${selectedCatalog.slug}${qs ? `?${qs}` : ''}`;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <code style={{ fontSize: '11px', wordBreak: 'break-all', background: 'var(--bg-primary, #fff)', padding: '8px', borderRadius: '6px', display: 'block' }}>{filteredUrl}</code>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-primary" style={{ flex: 1 }} onClick={async () => { await copyToClipboard(filteredUrl); toast.success('تم نسخ الرابط المفلتر!'); }}>
                            <Copy size={14} /> نسخ الرابط المفلتر
                          </button>
                          <button className="share-btn whatsapp" style={{ flex: 1, borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}
                            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${selectedCatalog.name}\n\n${filteredUrl}`)}`, '_blank')}>
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            واتساب
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="share-options">
                <h4>مشاركة عبر</h4>
                <div className="share-buttons">
                  <button className="share-btn whatsapp" onClick={() => handleShareWhatsApp(selectedCatalog)}>
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    واتساب
                  </button>
                  <button className="share-btn twitter" onClick={() => handleShareTwitter(selectedCatalog)}>
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    تويتر
                  </button>
                  <button className="share-btn qr" onClick={() => handleDownloadQR(selectedCatalog)}><QrCode size={24} />QR Code</button>
                  <button className="share-btn link" onClick={() => handleOpenCatalogLink(selectedCatalog)}><ExternalLink size={24} />فتح الرابط</button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={handleCloseShareModal}>إغلاق</button>
            </div>
          </div>
        </div>
      )}

      {showSectionFiltersModal && editingSectionFilters && (
        <div className="modal-overlay" onClick={() => setShowSectionFiltersModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Settings size={20} />إعدادات القسم</h2>
              <button className="modal-close" onClick={() => setShowSectionFiltersModal(false)}><X size={24} /></button>
            </div>
            <div className="modal-body">
              {/* اسم القسم */}
              <div className="form-section">
                <div className="form-group">
                  <label>اسم القسم</label>
                  <input
                    type="text"
                    className="form-control"
                    value={sectionSettingsName}
                    onChange={(e) => setSectionSettingsName(e.target.value)}
                    placeholder="مثال: عروض الفلل، أراضي سكنية..."
                  />
                </div>
              </div>

              {editingSectionFilters.sectionType === 'manual' ? (
                /* ─── وضع يدوي: اختيار عروض من القائمة ─── */
                <div className="form-section">
                  <h3>اختر العروض يدوياً</h3>
                  <p className="section-desc">اختر العروض التي تريد أن تظهر في هذا القسم.</p>
                  <div className="manual-filter-row">
                    <select
                      className="form-control"
                      value={manualCityFilter}
                      onChange={(e) => setManualCityFilter(e.target.value)}
                    >
                      <option value="">كل المدن</option>
                      {locationCities.map((c) => <option key={c._id || c.value} value={c.arLabel || c.value}>{c.arLabel || c.label || c.value}</option>)}
                    </select>
                    <select
                      className="form-control"
                      value={manualTypeFilter}
                      onChange={(e) => setManualTypeFilter(e.target.value)}
                    >
                      <option value="">كل الأنواع</option>
                      {propertyTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ابحث بالعنوان أو الحي..."
                    value={manualOfferSearch}
                    onChange={(e) => setManualOfferSearch(e.target.value)}
                    style={{ marginBottom: '12px' }}
                  />
                  <div className="manual-offers-list">
                    {offersPoolLoading ? (
                      <div className="section-desc">جاري تحميل العروض...</div>
                    ) : offersPool.filter((o) => {
                      if (manualCityFilter && o.city !== manualCityFilter) return false;
                      if (manualTypeFilter && (o.propertyType || o.type) !== manualTypeFilter) return false;
                      if (!manualOfferSearch.trim()) return true;
                      const q = manualOfferSearch.toLowerCase();
                      return (o.title || '').toLowerCase().includes(q) || (o.district || '').toLowerCase().includes(q);
                    }).length === 0 ? (
                      <div className="section-desc">لا توجد عروض مطابقة.</div>
                    ) : (
                      offersPool.filter((o) => {
                        if (manualCityFilter && o.city !== manualCityFilter) return false;
                        if (manualTypeFilter && (o.propertyType || o.type) !== manualTypeFilter) return false;
                        if (!manualOfferSearch.trim()) return true;
                        const q = manualOfferSearch.toLowerCase();
                        return (o.title || '').toLowerCase().includes(q) || (o.district || '').toLowerCase().includes(q);
                      }).map((offer) => {
                        const oid = offer._id || offer.id;
                        const isSelected = manualSelectedOfferIds.includes(oid);
                        return (
                          <label key={oid} className={`manual-offer-row ${isSelected ? 'selected' : ''}`}>
                            {/* checkbox on the left (RTL: start side) */}
                            <input
                              className="manual-offer-checkbox"
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setManualSelectedOfferIds((prev) =>
                                  isSelected ? prev.filter((i) => i !== oid) : [...prev, oid]
                                );
                              }}
                            />
                            {/* title + meta in the middle */}
                            <span className="manual-offer-info">
                              <span className="manual-offer-title">{offer.title || 'بدون عنوان'}</span>
                              <span className="manual-offer-meta">
                                {offer.city && <span>{offer.city}</span>}
                                {offer.price > 0 && <span>{Number(offer.price).toLocaleString('ar-SA')} ر.س</span>}
                              </span>
                            </span>
                            {/* thumbnail on the right (RTL: end side) */}
                            <span className="manual-offer-thumb">
                              {(offer.coverImage || (offer.images && offer.images[0])) ? (
                                <img src={offer.coverImage || offer.images[0]} alt={offer.title || 'عرض'} />
                              ) : (
                                <span className="manual-offer-thumb-placeholder" />
                              )}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    تم اختيار <strong>{manualSelectedOfferIds.length}</strong> عرض
                  </div>
                </div>
              ) : (
                /* ─── وضع فلاتر ─── */
                <>
                  <div className="section-how-it-works">
                    <span className="how-icon">💡</span>
                    <div>
                      <strong>كيف تعمل الأقسام؟</strong>
                      <p>العروض تُضاف للقسم <strong>تلقائياً</strong> بناءً على الفلاتر أدناه. مثال: إذا اخترت "نوع العقار = فيلا" فستظهر جميع عروض الفيلل في هذا القسم.</p>
                      <p style={{marginTop:'4px', color:'var(--danger-color, #dc3545)'}}>بدون فلاتر = القسم يظهر فارغاً دائماً.</p>
                    </div>
                  </div>

                  {renderFiltersEditor(
                    { filters: sectionFiltersDraft },
                    (updater) => {
                      setSectionFiltersDraft((prev) => {
                        const next = typeof updater === 'function' ? updater({ filters: prev }) : updater;
                        return next.filters || prev;
                      });
                    }
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              {editingSectionFilters.sectionType !== 'manual' && (
                <button className="btn btn-outline" onClick={() => setSectionFiltersDraft({ ...DEFAULT_CATALOG_FILTERS })}>مسح الفلاتر</button>
              )}
              <button className="btn btn-outline" onClick={() => setShowSectionFiltersModal(false)}>إلغاء</button>
              <button className="btn btn-primary" onClick={handleSaveSectionFilters} disabled={saving || !sectionSettingsName.trim()}>
                {saving ? <Loader className="spinner" size={18} /> : <Check size={18} />}
                حفظ الإعدادات
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </PermissionGate>
  );
};

export default CatalogPage;
